/*
  SAVRA newsletter overlay controller. Real Shopify {% form 'customer' %}
  submission (full-page POST, no Ajax interception, no faked success).
  This script only handles: when to show the modal (timed/scroll trigger),
  frequency capping via localStorage, coexistence with the other SAVRA
  overlays (mega menu, search, mobile drawer, cart drawer), and a Shopify
  Theme Editor preview mode (window.Shopify.designMode, the official
  detection global) that opens the overlay deterministically when the
  merchant selects this section, bypassing timing/frequency rules for
  that preview session only. Editor preview never writes the real
  dismissal/subscribed localStorage keys that customer visits use.
*/
(function () {
  'use strict';

  var DISMISSED_KEY = 'sav_newsletter_dismissed_until';
  var SUBSCRIBED_KEY = 'sav_newsletter_subscribed';

  function trapFocus(container, onEscape) {
    var focusable = container.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (!focusable.length) return function () {};
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    function handleKeydown(e) {
      if (e.key === 'Escape' && onEscape) { onEscape(); return; }
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    container.addEventListener('keydown', handleKeydown);
    return function () { container.removeEventListener('keydown', handleKeydown); };
  }

  function readStorage(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function writeStorage(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* private mode or disabled storage: fail silently, never block the form */ }
  }

  function isSuppressed() {
    if (readStorage(SUBSCRIBED_KEY) === 'true') return true;
    var until = readStorage(DISMISSED_KEY);
    if (until && Date.now() < Number(until)) return true;
    return false;
  }

  // Overlay priority model: mega menu, search, mobile drawer, and cart
  // drawer all outrank the newsletter overlay. Each of those modules
  // closes the newsletter overlay itself when it opens (theme.js,
  // cart-drawer.js); this checks the reverse direction, so the
  // newsletter never opens ON TOP of one already open.
  function anyHigherPriorityOverlayOpen() {
    var mega = document.querySelector('[data-mega-item].is-open');
    var drawer = document.getElementById('MobileDrawer');
    var search = document.getElementById('SearchDrawer');
    var cart = document.querySelector('[data-cart-drawer-panel]');
    return !!(
      mega ||
      (drawer && drawer.classList.contains('is-open')) ||
      (search && search.classList.contains('is-open')) ||
      (cart && cart.classList.contains('is-open'))
    );
  }

  var NewsletterOverlay = (function () {
    function NewsletterOverlay(root) {
      this.root = root;
      this.panel = root.querySelector('[data-newsletter-panel]');
      this.scrim = root.querySelector('[data-newsletter-scrim]');
      this.closeBtn = root.querySelector('[data-newsletter-close]');
      this.delaySeconds = Number(root.getAttribute('data-delay')) || 25;
      this.scrollPct = Number(root.getAttribute('data-scroll')) || 40;
      this.dismissDays = Number(root.getAttribute('data-dismiss-days')) || 7;
      this.posted = root.getAttribute('data-posted') === 'true';
      this.hasErrors = root.getAttribute('data-has-errors') === 'true';
      this.designMode = !!(window.Shopify && window.Shopify.designMode);
      this.triggered = false;
      this.releaseFocusTrap = null;
      this.timerId = null;

      this.bind();
      window.savraNewsletterOverlay = this;

      if (this.posted) {
        writeStorage(SUBSCRIBED_KEY, 'true');
        this.root.hidden = false;
        this.open();
        this.root.dispatchEvent(new CustomEvent('sav:newsletter:success', { bubbles: true }));
        var self = this;
        setTimeout(function () { self.close(); }, 4500);
      } else if (this.hasErrors) {
        this.root.hidden = false;
        this.open();
      } else if (this.designMode) {
        // Theme Editor: no timer/scroll trigger, no frequency-cap check.
        // Stays hidden until the merchant explicitly selects this section
        // (see the shopify:section:select listener at the bottom of this
        // file), which calls previewOpen() directly.
      } else if (!isSuppressed()) {
        this.root.hidden = false;
        this.schedule();
      }
    }

    NewsletterOverlay.prototype.bind = function () {
      var self = this;
      this.closeBtn.addEventListener('click', function () { self.dismiss(); });
      this.scrim.addEventListener('click', function () { self.dismiss(); });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.panel.classList.contains('is-open')) self.dismiss();
      });
      var form = this.root.querySelector('form');
      if (form) {
        form.addEventListener('submit', function () {
          // Real Shopify submission, full-page POST — this only fires a
          // hook event, it never intercepts or fakes the request.
          self.root.dispatchEvent(new CustomEvent('sav:newsletter:submit', { bubbles: true }));
        });
      }
    };

    NewsletterOverlay.prototype.schedule = function () {
      var self = this;
      this.timerId = setTimeout(function () { self.attemptOpen(); }, this.delaySeconds * 1000);

      function onScroll() {
        var doc = document.documentElement;
        var scrolled = window.scrollY || doc.scrollTop;
        var max = (doc.scrollHeight - doc.clientHeight) || 1;
        var pct = (scrolled / max) * 100;
        if (pct >= self.scrollPct) {
          window.removeEventListener('scroll', onScroll);
          self.attemptOpen();
        }
      }
      window.addEventListener('scroll', onScroll, { passive: true });
    };

    NewsletterOverlay.prototype.attemptOpen = function (attempt) {
      var self = this;
      if (this.triggered || isSuppressed()) return;
      if (anyHigherPriorityOverlayOpen()) {
        // Defer rather than interrupt an active overlay. Bounded retry:
        // a few short checks, then give up quietly for this page load.
        var tries = attempt || 0;
        if (tries >= 5) return;
        setTimeout(function () { self.attemptOpen(tries + 1); }, 2000);
        return;
      }
      this.triggered = true;
      if (this.timerId) clearTimeout(this.timerId);
      this.open();
    };

    // Theme Editor only: deterministic open on section select, ignoring
    // the timer/scroll trigger and the frequency-cap check entirely.
    NewsletterOverlay.prototype.previewOpen = function () {
      this.root.hidden = false;
      this.open();
    };

    NewsletterOverlay.prototype.open = function () {
      var self = this;
      this.panel.classList.add('is-open');
      this.scrim.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      this.releaseFocusTrap = trapFocus(this.panel, function () { self.dismiss(); });
      var emailInput = this.panel.querySelector('#NewsletterOverlayEmail');
      (emailInput || this.closeBtn).focus();
      this.root.dispatchEvent(new CustomEvent('sav:newsletter:open', { bubbles: true }));
    };

    NewsletterOverlay.prototype.close = function () {
      this.panel.classList.remove('is-open');
      this.scrim.classList.remove('is-open');
      document.body.style.overflow = '';
      if (this.releaseFocusTrap) this.releaseFocusTrap();
    };

    NewsletterOverlay.prototype.dismiss = function () {
      this.close();
      if (!this.designMode) {
        // Theme Editor closes (deselecting the section, clicking the
        // close button while previewing, Escape) are configuration
        // actions, not customer behavior — never write the real
        // dismissal-suppression timestamp from inside the editor.
        var until = Date.now() + this.dismissDays * 24 * 60 * 60 * 1000;
        writeStorage(DISMISSED_KEY, String(until));
      }
      this.root.dispatchEvent(new CustomEvent('sav:newsletter:dismiss', { bubbles: true }));
    };

    return NewsletterOverlay;
  })();

  // sectionSelected persists across shopify:section:load re-renders (the
  // Theme Editor swaps this section's HTML in place on a setting change;
  // this IIFE's own script tag is not re-run, so a module-level variable
  // survives that swap even though the DOM node and NewsletterOverlay
  // instance underneath it do not).
  var currentInstance = null;
  var sectionSelected = false;

  function boot() {
    var root = document.querySelector('sav-newsletter-overlay');
    if (!root) return;
    currentInstance = new NewsletterOverlay(root);
    if (sectionSelected && currentInstance.designMode) currentInstance.previewOpen();
  }

  boot();

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      if (e.detail && e.detail.sectionId === 'newsletter-overlay') boot();
    });
    document.addEventListener('shopify:section:select', function (e) {
      if (e.detail && e.detail.sectionId === 'newsletter-overlay') {
        sectionSelected = true;
        if (currentInstance) currentInstance.previewOpen();
      }
    });
    document.addEventListener('shopify:section:deselect', function (e) {
      if (e.detail && e.detail.sectionId === 'newsletter-overlay') {
        sectionSelected = false;
        if (currentInstance) currentInstance.close();
      }
    });
  }
})();
